<template>
  <section class="press-section" aria-label="Edge vs. the Market">
    <div class="press-section__inner">
      <div class="section__header">
        <div class="section-number">{{ sectionNumber }}</div>
        <div class="section__titles">
          <p class="eyebrow">
            Model vs. Market<template v-if="market.line_kind">
              &mdash;
              <span class="line-kind" :class="`line-kind--${market.line_kind}`">
                {{ lineKindLabel }}
              </span></template
            >
          </p>
          <h2 class="display-lg">Where we <em>disagree</em> with the book.</h2>
          <p class="body-lg section__dek">
            Simulated probability against the de-vigged market price. Positive edge favors the bet;
            negative means the market is richer than our model.
          </p>
        </div>
      </div>

      <div class="rule-toggle-row">
        <hr class="rule rule--double rule--flex" />
        <button
          class="toggle-btn numeric"
          :aria-label="`Show ${showPrice ? 'percent' : 'price'}`"
          @click="showPrice = !showPrice"
        >
          {{ showPrice ? '%' : '±' }}
        </button>
      </div>

      <div class="edge-grid">
        <!-- Moneyline + Total (only when game lines exist) -->
        <article v-if="hasGameLines" class="paper-card edge-card">
          <header class="card__header">
            <span class="card__n numeric">&Ntilde; 01</span>
            <h3 class="card__title">Game Lines</h3>
            <span class="card__tag">Moneyline &amp; total</span>
          </header>

          <!-- Live score row (non-final games only) -->
          <div v-if="isLive && hasScore" class="live-score-row">
            <span class="live-badge">Live</span>
            <span class="live-score__team">{{ awayAbbr }}</span>
            <span class="live-score__val numeric">{{ awayScore }}</span>
            <span class="live-score__sep">&ndash;</span>
            <span class="live-score__val numeric">{{ homeScore }}</span>
            <span class="live-score__team">{{ homeAbbr }}</span>
          </div>

          <table class="edge-table">
            <thead>
              <tr>
                <th scope="col">Market</th>
                <th scope="col" class="th--num">Price</th>
                <th scope="col" class="th--num">{{ showPrice ? 'Impl Price' : 'Implied' }}</th>
                <th scope="col" class="th--num">{{ showPrice ? 'Sim Price' : 'Sim' }}</th>
                <th v-if="isClosing" scope="col" class="th--num">Edge</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="market.moneyline_away">
                <td>{{ awayAbbr }} ML</td>
                <td class="th--num numeric">{{ formatOdds(market.moneyline_away.best_price) }}</td>
                <td class="th--num numeric">
                  {{ impliedDisplay(market.moneyline_away.implied_pct) }}
                </td>
                <td class="th--num numeric">{{ simDisplay(market.moneyline_away.sim_pct) }}</td>
                <td
                  v-if="isClosing"
                  class="th--num numeric"
                  :class="edgeClass(market.moneyline_away.edge)"
                >
                  {{ formatEdge(market.moneyline_away.edge) }}
                </td>
              </tr>
              <tr v-if="market.moneyline_home">
                <td>{{ homeAbbr }} ML</td>
                <td class="th--num numeric">{{ formatOdds(market.moneyline_home.best_price) }}</td>
                <td class="th--num numeric">
                  {{ impliedDisplay(market.moneyline_home.implied_pct) }}
                </td>
                <td class="th--num numeric">{{ simDisplay(market.moneyline_home.sim_pct) }}</td>
                <td
                  v-if="isClosing"
                  class="th--num numeric"
                  :class="edgeClass(market.moneyline_home.edge)"
                >
                  {{ formatEdge(market.moneyline_home.edge) }}
                </td>
              </tr>
              <tr v-if="market.total">
                <td>Over {{ market.total.line }}</td>
                <td class="th--num numeric">{{ formatOdds(market.total.over_price) }}</td>
                <td class="th--num numeric">{{ impliedDisplay(market.total.implied_over_pct) }}</td>
                <td class="th--num numeric">{{ simDisplay(market.total.sim_over_pct) }}</td>
                <td v-if="isClosing" class="th--num numeric" :class="edgeClass(market.total.edge)">
                  {{ formatEdge(market.total.edge) }}
                </td>
              </tr>
            </tbody>
          </table>

          <p v-if="!isClosing" class="edge-card__note">
            Edge vs. pre-game lines only &mdash; we don&rsquo;t re-sim from live state.
          </p>
        </article>

        <!-- Best prop bets -->
        <article v-if="market.top_prop_edges.length" class="paper-card edge-card">
          <header class="card__header">
            <span class="card__n numeric">&Ntilde; 02</span>
            <h3 class="card__title">Best Prop Bets</h3>
            <span class="card__tag">Ranked by edge</span>
          </header>
          <table class="edge-table">
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col">Market</th>
                <th scope="col" class="th--num">Line</th>
                <th scope="col" class="th--num">{{ showPrice ? 'Sim Price' : 'Sim O' }}</th>
                <th scope="col" class="th--num">{{ showPrice ? 'Impl Price' : 'Impl O' }}</th>
                <th scope="col" class="th--num">Edge</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in market.top_prop_edges" :key="propKey(p)">
                <td>{{ p.player_name }}</td>
                <td>{{ propMarketLabel(p.market) }}</td>
                <td class="th--num numeric">{{ p.line }}</td>
                <td class="th--num numeric">{{ simDisplay(p.sim_over_pct) }}</td>
                <td class="th--num numeric">{{ impliedDisplay(p.implied_over_pct) }}</td>
                <td class="th--num numeric" :class="edgeClass(p.edge)">
                  {{ formatEdge(p.edge) }}
                </td>
              </tr>
            </tbody>
          </table>
          <p class="edge-card__note">
            Edge sign favors the Over (+) or Under (&minus;). Markets the sim doesn&rsquo;t model
            (outs, total bases, steals) are omitted.
          </p>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { MarketEdges, PropEdge } from '@/api/games'
import {
  formatOdds,
  formatPct,
  formatPctAsOdds,
  formatEdge,
  edgeClass,
  propMarketLabel
} from '@/utils/odds'
import { computed, ref } from 'vue'

const props = defineProps<{
  market: MarketEdges
  homeAbbr: string
  awayAbbr: string
  sectionNumber: string
  gameStatus: string | null
  homeScore: number | null
  awayScore: number | null
}>()

const showPrice = ref(false)

const lineKindLabel = computed(() => {
  if (props.market.line_kind === 'closing') return 'Closing Line'
  if (props.market.line_kind === 'current') return 'Current Line'
  return ''
})

const isClosing = computed(() => props.market.line_kind === 'closing')

const isFinal = computed(() => props.gameStatus?.toLowerCase() === 'final')

const isLive = computed(() => !isFinal.value && props.gameStatus != null)

const hasScore = computed(() => props.homeScore !== null && props.awayScore !== null)

const hasGameLines = computed(
  () =>
    props.market.moneyline_home !== null ||
    props.market.moneyline_away !== null ||
    props.market.total !== null
)

function impliedDisplay(pct: number | null): string {
  return showPrice.value ? formatPctAsOdds(pct) : formatPct(pct)
}

function simDisplay(pct: number | null): string {
  return showPrice.value ? formatPctAsOdds(pct) : formatPct(pct)
}

function propKey(p: PropEdge): string {
  return `${p.player_id}-${p.market}-${p.line}`
}
</script>

<style scoped>
/* Section frame — matches SimulateView's .press-section rules, which are
   scoped to that view and so don't reach this child component. Keeps the
   panel's max-width and 4rem/2rem gutters aligned with the page sections. */
.press-section {
  background: transparent;
  border-bottom: var(--rule-thin) solid var(--paper-edge);
}

.press-section__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.edge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: 1.5rem;
}

.edge-card__note {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  font-style: italic;
  opacity: 0.7;
}

.edge-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.edge-table th,
.edge-table td {
  padding: 0.4rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid var(--rule-soft, rgba(0, 0, 0, 0.1));
}

.edge-table .th--num {
  text-align: right;
}

.line-kind {
  font-weight: 600;
}

.line-kind--closing {
  color: var(--ink-accent, #1a5c38);
}

.line-kind--current {
  color: var(--ink-muted, #6b6b6b);
}

/* Edge coloring — mirrors the games schedule card. */
:deep(.edge--positive),
.edge--positive {
  color: var(--edge-positive, #1a7f3c);
  font-weight: 600;
}

:deep(.edge--negative),
.edge--negative {
  color: var(--edge-negative, #b23b3b);
}

/* Rule + toggle button on the same line, above the card grid */
.rule-toggle-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.rule--flex {
  flex: 1;
  margin: 0;
}

/* Percent / price toggle button */
.toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border: var(--rule-thin) solid var(--ink);
  background: transparent;
  color: var(--ink);
  font-family: var(--font-data);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
  flex-shrink: 0;
}

.toggle-btn:hover {
  background: var(--ink);
  color: var(--paper);
}

/* Live score banner */
.live-score-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.75rem;
  background: var(--paper-deep);
  border-bottom: var(--rule-thin) solid var(--paper-edge);
  font-family: var(--font-data);
  font-size: 0.8rem;
}

.live-badge {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--paper);
  background: var(--ox);
  padding: 0.1rem 0.35rem;
  margin-right: 0.35rem;
  flex-shrink: 0;
}

.live-score__team {
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--ink-medium);
}

.live-score__val {
  font-weight: 700;
  color: var(--ink);
  min-width: 1.2rem;
  text-align: center;
}

.live-score__sep {
  color: var(--ink-ghost);
  font-weight: 400;
}

@media (max-width: 700px) {
  .press-section__inner {
    padding: 3rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
